import Str from '@libs/common/str';
import type {
  CharacterLimitParams,
  TranslationBase,
  UntilTimeParams,
} from './types';
import type {
  BadgesDayCountParams,
  BreakdownCenterUnitsParams,
  BreakdownDrinkLabelParams,
  BreakdownPeriodParams,
  BreakdownSliceCaptionParams,
  BreakdownTileSubtitleParams,
  CommonFriendsLabelParams,
  ConfirmWithProviderPromptParams,
  DiscardSessionParams,
  DrinkingSessionsParams,
  ForgotPasswordSuccessParams,
  FriendRequestsCountParams,
  InviteDisplayNameParams,
  InviteLinkParams,
  FriendCountParams,
  LastSessionSummaryParams,
  OnboardingStepCounterParams,
  QuickAddDrinkParams,
  RelativeTimeAgoParams,
  SessionConfirmTimezoneChangeParams,
  SessionDefaultNameParams,
  SessionPhotoLimitParams,
  SessionPhotoParams,
  SessionStartTimeParams,
  SessionWindowIdParams,
  StatsDrillDownTitleParams,
  StatsThresholdParams,
  SupporterCancelledStatusParams,
  SupporterPriceParams,
  SupporterPurchaseCtaParams,
  SupporterPurchaseErrorParams,
  SupporterRenewalDateParams,
  SupporterTipCountParams,
  TipJarPromptTitleParams,
  TipJarPromptDevStatusParams,
  UnitCountParams,
  UpdateEmailSentEmailParams,
  VerifyEmailScreenEmailParmas,
  WeekOfParams,
} from './params';

export default {
  common: {
    cancel: 'Cancel',
    dismiss: 'Dismiss',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',
    askMeLater: 'Ask Me Later',
    buttonConfirm: 'Got it',
    name: 'Name',
    attachment: 'Attachment',
    to: 'To',
    or: 'Or',
    optional: 'Optional',
    ago: 'Ago',
    new: 'New',
    search: 'Search',
    searchWithThreeDots: 'Search...',
    textInputField: 'Text input field',
    next: 'Next',
    previous: 'Previous',
    goBack: 'Go back',
    logIn: 'Log in',
    logInHere: 'Log in',
    signUp: 'Sign up',
    signUpHere: 'Sign up',
    create: 'Create',
    add: 'Add',
    resend: 'Resend',
    save: 'Save',
    select: 'Select',
    saveChanges: 'Save changes',
    submit: 'Submit',
    rotate: 'Rotate',
    zoom: 'Zoom',
    password: 'Password',
    magicCode: 'Magic code',
    twoFactorCode: 'Two-factor code',
    workspaces: 'Workspaces',
    chats: 'Chats',
    group: 'Group',
    profile: 'Profile',
    account: 'Account',
    username: 'Username',
    referral: 'Referral',
    payments: 'Payments',
    wallet: 'Wallet',
    clear: 'Clear',
    preferences: 'Preferences',
    view: 'View',
    not: 'Not',
    unknown: 'Unknown',
    authentication: 'Authentication',
    signIn: 'Sign in',
    google: 'Google',
    apple: 'Apple',
    signInWithGoogle: 'Sign in with Google',
    signInWithApple: 'Sign in with Apple',
    signInWith: 'Sign in with',
    continue: 'Continue',
    createAccount: 'Create account',
    getStarted: 'Get started',
    firstName: 'First name',
    lastName: 'Last name',
    displayName: 'Display name',
    nickname: 'Nickname',
    phone: 'Phone',
    phoneNumber: 'Phone number',
    phoneNumberPlaceholder: '(xxx) xxx-xxx-xxx',
    email: 'Email',
    and: 'and',
    details: 'Details',
    privacy: 'Privacy',
    hidden: 'Hidden',
    visible: 'Visible',
    delete: 'Delete',
    discard: 'Discard',
    archived: 'archived',
    contacts: 'Contacts',
    recents: 'Recents',
    close: 'Close',
    loading: 'Loading',
    download: 'Download',
    downloading: 'Downloading',
    warning: 'Warning',
    manage: 'Manage',
    pin: 'Pin',
    unPin: 'Unpin',
    back: 'Back',
    yesIKnowWhatIAmDoing: 'Yes, I know what I am doing',
    saveAndContinue: 'Save & continue',
    settings: 'Settings',
    termsOfService: 'Terms of Service',
    kirokuTermsOfService: 'Kiroku Terms of Service',
    privacyPolicy: 'Privacy Policy',
    subscriptionTerms: 'Subscription Terms',
    members: 'Members',
    invite: 'Invite',
    here: 'here',
    date: 'Date',
    dob: 'Date of birth',
    gender: 'Gender',
    weight: 'Weight',
    currentYear: 'Current year',
    currentMonth: 'Current month',
    city: 'City',
    state: 'State',
    streetAddress: 'Street address',
    stateOrProvince: 'State / Province',
    country: 'Country',
    zip: 'Zip code',
    zipPostCode: 'Zip / Postcode',
    whatThis: "What's this?",
    iAcceptThe: 'I accept the ',
    remove: 'Remove',
    admin: 'Admin',
    owner: 'Owner',
    dateFormat: 'YYYY-MM-DD',
    send: 'Send',
    great: 'Great!',
    notifications: 'Notifications',
    na: 'N/A',
    noResultsFound: 'No results found',
    recentDestinations: 'Recent destinations',
    timePrefix: "It's",
    time: 'Time',
    units: 'Units',
    drinks: 'Drinks',
    conjunctionFor: 'for',
    todayAt: 'Today at',
    tomorrowAt: 'Tomorrow at',
    yesterdayAt: 'Yesterday at',
    success: 'Success',
    copiedToClipboard: 'Copied to clipboard',
    conjunctionAt: 'at',
    genericErrorMessage:
      'Oops... something went wrong and your request could not be completed. Please try again later.',
    error: {
      error: 'Error',
      unknown: 'Unknown error',
      invalidAmount: 'Invalid amount',
      acceptTerms: 'You must accept the Terms of Service to continue',
      fieldRequired: 'This field is required.',
      requestModified: 'This request is being modified by another member.',
      characterLimit: ({limit}: CharacterLimitParams) =>
        `Exceeds the maximum length of ${limit} characters`,
      characterLimitExceedCounter: ({length, limit}) =>
        `Character limit exceeded (${length}/${limit})`,
      dateInvalid: 'Please select a valid date',
      invalidDateShouldBeFuture: 'Please choose today or a future date.',
      invalidTimeShouldBeFuture:
        'Please choose a time at least one minute ahead.',
      invalidCharacter: 'Invalid character',
      enterAmount: 'Enter an amount',
      enterDate: 'Enter a date',
      userNull: 'We could not find your user. Please log in again.',
      notFound: 'Not found',
      reauthenticationFailed: 'Reauthentication failed',
      sessionIdCreation: 'Failed to create a new session ID',
    },
    comma: 'comma',
    semicolon: 'semicolon',
    please: 'Please',
    contactUs: 'contact us',
    fixTheErrors: 'fix the errors',
    inTheFormBeforeContinuing: 'in the form before continuing',
    confirm: 'Confirm',
    reset: 'Reset',
    done: 'Done',
    more: 'More',
    join: 'Join',
    leave: 'Leave',
    decline: 'Decline',
    cantFindAddress: "Can't find your address? ",
    enterManually: 'Enter it manually',
    message: 'Message ',
    leaveRoom: 'Leave room',
    leaveThread: 'Leave thread',
    you: 'You',
    youAfterPreposition: 'you',
    your: 'your',
    youAppearToBeOffline: 'You appear to be offline.',
    thisFeatureRequiresInternet:
      'This feature requires an active internet connection to be used.',
    areYouSure: 'Are you sure?',
    verify: 'Verify',
    yesContinue: 'Yes, continue',
    websiteExample: 'e.g. https://www.kiroku.cz',
    description: 'Description',
    with: 'with',
    shareCode: 'Share code',
    share: 'Share',
    per: 'per',
    mi: 'mile',
    km: 'kilometer',
    copied: 'Copied!',
    someone: 'Someone',
    total: 'Total',
    edit: 'Edit',
    letsDoThis: `Let's do this!`,
    letsStart: `Let's start`,
    showMore: 'Show more',
    category: 'Category',
    tag: 'Tag',
    receipt: 'Receipt',
    replace: 'Replace',
    distance: 'Distance',
    mile: 'mile',
    miles: 'miles',
    kilometer: 'kilometer',
    kilometers: 'kilometers',
    recent: 'Recent',
    all: 'All',
    am: 'AM',
    pm: 'PM',
    tbd: 'TBD',
    card: 'Card',
    whyDoWeAskForThis: 'Why do we ask for this?',
    required: 'Required',
    showing: 'Showing',
    of: 'of',
    default: 'Default',
    update: 'Update',
    member: 'Member',
    role: 'Role',
    note: 'Note',
    blackout: 'Blackout',
    timezone: 'Timezone',
  },
  modal: {
    backdropLabel: 'Close',
  },
  calendar: {
    monthNames: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    monthNamesShort: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
    dayNames: [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ],
    dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    today: 'Today',
    fullscreenTitle: 'Drinking sessions',
    loadingOlderMonths: 'Loading older months…',
    monthTotalUnits: ({unitCount}: UnitCountParams) =>
      `${unitCount} ${Str.pluralize('unit', 'units', unitCount)}`,
    dayTotalUnits: ({unitCount}: UnitCountParams) =>
      `${unitCount} ${Str.pluralize('unit', 'units', unitCount)}`,
  },
  textInput: {
    accessibilityLabel: 'Text input',
    resetSearch: 'Reset search',
  },
  bottomTabBar: {
    home: 'Home',
    friends: 'Friends',
    profile: 'Profile',
    settings: 'Settings',
    badges: 'Badges',
    statistics: 'Statistics',
    menu: 'Menu',
  },
  drinks: {
    // The keys are in snake_case to match the API
    smallBeer: 'Small Beer',
    beer: 'Beer',
    wine: 'Wine',
    weakShot: 'Weak Shot',
    strongShot: 'Strong Shot',
    cocktail: 'Cocktail',
    other: 'Other',
  },
  units: {
    light: 'Light',
    moderate: 'Moderate',
  },
  timePeriods: {
    never: 'Never',
    thirtyMinutes: '30 minutes',
    oneHour: '1 hour',
    afterToday: 'Today',
    afterWeek: 'A week',
    custom: 'Custom',
    untilTomorrow: 'Until tomorrow',
    untilTime: ({time}: UntilTimeParams) => `Until ${time}`,
    fullSingle: {
      second: 'second',
      minute: 'minute',
      hour: 'hour',
      day: 'day',
      week: 'week',
      month: 'month',
      year: 'year',
    },
    fullPlural: {
      second: 'seconds',
      minute: 'minutes',
      hour: 'hours',
      day: 'days',
      week: 'weeks',
      month: 'months',
      year: 'years',
    },
    abbreviated: {
      second: 's',
      minute: 'm',
      hour: 'h',
      day: 'd',
      week: 'w',
      month: 'M',
      year: 'Y',
    },
  },
  session: {
    people: {
      selectAll: 'Select all',
    },
    offlineMessageRetry:
      "Looks like you're offline. Please check your connection and try again.",
  },
  agreeToTerms: {
    title: "We've updated our terms of service",
    description:
      'We encourage you to read the updated terms of service and privacy policy in full.',
    iHaveRead:
      'I have read and agree to the terms of service and privacy policy',
    mustAgree:
      'You must agree to the terms of service and privacy policy before continuing.',
  },
  location: {
    useCurrent: 'Use current location',
    notFound:
      'We were unable to find your location, please try again or enter an address manually.',
    permissionDenied:
      'It looks like you have denied permission to your location.',
    please: 'Please',
    allowPermission: 'allow location permission in settings',
    tryAgain: 'and then try again.',
  },
  imageUpload: {
    uploadSuccess: 'Image uploaded successfully!',
    uploadingImage: 'Uploading image...',
    uploadFinished: 'Upload finished!',
    pleaseReload: 'Please reload the app to see changes.',
    chooseProfilePicture: 'Choose a profile picture',
  },
  sessionPhotos: {
    title: 'Photos',
    addPhoto: 'Add a photo',
    uploading: 'Adding your photo...',
    photo: ({index, count}: SessionPhotoParams) => `Photo ${index} of ${count}`,
    limitReached: ({limit}: SessionPhotoLimitParams) =>
      `A session can hold ${limit} photos.`,
    remove: 'Remove this photo',
    removePrompt: 'Remove this photo from the session? This cannot be undone.',
    report: 'Report this photo',
    error: {
      load: 'We could not load the photos for this session.',
    },
  },
  storage: {
    permissionDenied: 'Storage Permission Required',
    appNeedsAccess:
      'App needs access to your storage to read files. Please go to app settings and grant permission.',
    openSettings: 'Open Settings',
  },
  permissions: {
    permissionDenied: 'Permission Denied',
    youNeedToGrantPermission:
      'You need to grant permission for this functionality to work.',
    appNeedsAccess:
      'Kiroku needs this permission to continue. Open app settings and grant access.',
    openSettings: 'Open Settings',
    camera: {
      title: 'Camera Access Required',
      message: 'This app needs access to your camera for taking pictures.',
    },
    read_photos: {
      title: 'Access to Photos Required',
      message: 'This app requires access to your photos to function properly.',
    },
    write_photos: {
      title: 'Write Photos Access Required',
      message: 'This app needs access to write photos to your device.',
    },
    notifications: {
      title: 'Notification Access Required',
      message: 'This app needs access to send notifications to your device.',
    },
    location: {
      title: 'Location Access Required',
      message:
        'Kiroku can tag drinks in live sessions with your current location. Capture only happens while a live session is open and this preference is enabled.',
    },
  },
  personalDetails: {
    error: {
      hasInvalidCharacter: 'Invalid character',
      containsReservedWord: 'This name contains a reserved word.',
      containsProfanity:
        'This name contains inappropriate language. Please choose another.',
      characterLimitExceedCounter: ({length, limit}) =>
        `Character limit exceeded (${length}/${limit})`,
      characterLimit: ({limit}: CharacterLimitParams) =>
        `Exceeds the maximum length of ${limit} characters`,
      requiredFirstName: 'First name cannot be empty',
      requiredLastName: 'Last name cannot be empty',
      requiredDisplayName: 'Nickname cannot be empty',
    },
  },
  searchResult: {
    self: 'You',
    friend: 'Already a friend',
    sent: 'Awaiting a response',
    accept: 'Accept friend request',
    add: 'Send a request',
    blocked: 'Blocked',
  },
  socialScreen: {
    title: 'Friends',
    friendList: 'Friend List',
    friendSearch: 'Friend Search',
    friendRequests: 'Friend Requests',
    noFriendsYet: 'No friends yet',
    noFriendsYetDetail: 'Invite friends with your code, or find them by name.',
    inviteFriends: 'Invite friends',
    searchByName: 'Search by name',
    addFriends: 'Add friends',
  },
  friendsFriendsScreen: {
    title: 'Find Friends of Friends',
    seeProfile: 'See profile',
    searchUsersFriends: "Search for the user's friends",
    commonFriends: 'Common Friends',
    otherFriends: 'Other Friends',
    noFriendsFound: 'No friends found.',
    trySearching: 'Try searching for other users.',
    hasNoFriends: 'This user has not added any friends yet.',
  },
  friendSearchScreen: {
    noUsersFound: 'There are no users with this nickname.',
    searchWindow: "Search for a user's nickname",
  },
  friendRequestScreen: {
    requestsReceived: ({requestsCount}: FriendRequestsCountParams) =>
      `Requests Received (${requestsCount})`,
    requestsSent: ({requestsCount}: FriendRequestsCountParams) =>
      `Requests Sent (${requestsCount})`,
    lookingForNewFriends: 'Looking for new friends?',
    accept: 'Accept',
    remove: 'Remove',
    error: {
      userDoesNotExist: "This user doesn't exist.",
      couldNotAccept: 'Could not accept the friend request.',
      couldNotRemove: 'Could not remove the friend request.',
    },
  },
  friendListScreen: {
    searchYourFriendList: 'Search your friend list',
    userList: 'The list of your friends',
    offlineNoData:
      "You're offline. We couldn't load your friends. Reconnect and they'll show up here.",
  },
  friendAction: {
    error: {
      couldNotSendRequest:
        'Could not send the friend request. Please try again.',
      couldNotAcceptRequest:
        'Could not accept the friend request. Please try again.',
      couldNotRemoveRequest:
        'Could not remove the friend request. Please try again.',
      couldNotUnfriend: 'Could not remove this friend. Please try again.',
      couldNotBlockUser: 'Could not block this user. Please try again.',
    },
  },
  addFriendsScreen: {
    title: 'Add friends',
    yourCode: 'Your code',
    search: 'Search',
    friendNotOnKiroku: 'A friend does not have Kiroku yet?',
    inviteThemHere: 'Invite them here',
  },
  inviteCode: {
    cardCta: 'Scan to add me on Kiroku',
    friendCount: ({friendCount}: FriendCountParams) =>
      `${friendCount} ${Str.pluralize('friend', 'friends', friendCount)}`,
    explain:
      "Friends scan it with their phone camera and tap Add friend. After that, you'll see each other on the Friends tab.",
    qrCodeLabel: 'QR code with your invite link',
    share: 'Share invite link',
    shareMessage: ({link}: InviteLinkParams) =>
      `Add me as a friend on Kiroku: ${link}`,
    copyLink: 'Copy invite link',
    linkCopied: 'Link copied',
    resetLink: 'Reset invite link',
    resetDescription: 'Your current link and QR code will stop working.',
    resetTitle: 'Reset your invite link?',
    resetPrompt:
      "Your current link and QR code will stop working. Anyone who hasn't used it yet will need the new one.",
    resetConfirm: 'Reset',
    tryAgain: 'Try again',
    error: {
      couldNotLoad: "Couldn't load your invite link.",
      couldNotReset: "Couldn't reset your invite link. Please try again.",
    },
  },
  addFriendScreen: {
    title: 'Add friend',
    invitedYou: ({displayName}: InviteDisplayNameParams) =>
      `${displayName} invited you to be friends on Kiroku.`,
    addFriend: 'Add friend',
    addedTitle: 'Friend added',
    added: ({displayName}: InviteDisplayNameParams) =>
      `You and ${displayName} are now friends.`,
    alreadyFriends: ({displayName}: InviteDisplayNameParams) =>
      `You and ${displayName} are already friends.`,
    viewProfile: 'View profile',
    ownInvite: 'This is your own invite link. Share it so friends can add you.',
    showMyQrCode: 'Show my QR code',
    blocked: "You've blocked this user. Unblock them to add them as a friend.",
    manageBlockedUsers: 'Manage blocked users',
    signInToAdd: 'Sign in to add friend',
    signInHint:
      "Sign in or create an account and we'll bring you right back here.",
    getTheApp: 'Get the Kiroku app',
    availableOn: 'Available for iPhone and Android.',
    forYourIphone: 'For your iPhone, use the App Store.',
    forYourAndroid: 'For your Android phone, use Google Play.',
    appStoreBadge: 'Download on the App Store',
    googlePlayBadge: 'Get it on Google Play',
    invalidTitle: "This link doesn't work",
    invalid:
      'It may have been reset or mistyped. Ask your friend for a new one.',
    tryAgain: 'Try again',
    error: {
      couldNotLoad: "Couldn't load this invite.",
      couldNotAdd: "Couldn't add this friend.",
      generic: 'Something went wrong. Please try again.',
    },
  },
  notFoundScreen: {
    title: 'Not Found',
  },
  preferencesScreen: {
    title: 'Preferences',
    generalSection: {
      title: 'General',
      firstDayOfWeek: 'First day of the week',
      theme: "App's theme",
    },
    drinksAndUnitsSection: {
      title: 'Drinks and Units',
      description:
        "Set how many units each drink is worth and when the session's color changes",
      drinksToUnits: 'Drinks to units',
      unitsToColors: 'Units to colors',
      colorPalette: 'Color palette',
    },
    save: 'Save preferences',
    saving: 'Saving your preferences...',
    unsavedChanges:
      'You have unsaved changes. Are you sure you want to go back?',
    error: {
      save: "We couldn't save your preferences. Please try again.",
    },
  },
  locationPrompt: {
    title: 'Tag your drinks with a location',
    prompt:
      'Kiroku can tag each drink you log during a live session with your current location, so you can look back and see where each session happened. Your device will ask for location access next. You can change this anytime in Settings → Privacy.',
  },
  privacyScreen: {
    title: 'Privacy',
    friendsVisibilitySection: {
      title: 'Friends',
    },
    hideFromAllFriends: {
      label: 'Hide my data from all friends',
      description: 'When on, no friend can see your drinking sessions.',
    },
    diagnosticsSection: {
      title: 'Diagnostics',
    },
    crashReporting: {
      label: 'Send crash reports',
      description: 'Send crash reports so we can fix bugs faster.',
    },
    communicationsSection: {
      title: 'Communications',
    },
    emailMarketing: {
      label: 'Product news by email',
      description:
        'Get occasional news and tips about Kiroku by email. You can turn this off at any time.',
    },
    locationSection: {
      title: 'Location',
    },
    trackLocationDuringSessions: {
      label: 'Tag drinks with location',
      description: 'Tag each drink with where you logged it.',
    },
    clearLocationHistory: {
      label: 'Clear location history',
      description: 'Delete all saved locations. Your sessions and drinks stay.',
      button: 'Clear',
      confirmTitle: 'Clear location history?',
      confirmPrompt:
        'This will permanently delete every location attached to a drink in any of your sessions. The sessions themselves stay. You can re-enable location tagging at any time afterwards.',
      confirmAction: 'Clear',
      success: 'Your location history has been cleared.',
      error: "We couldn't clear your location history. Please try again.",
    },
    blockingSection: {
      title: 'Blocking',
    },
    blockedUsers: {
      label: 'Blocked users',
      description: 'Review and manage the people you have blocked.',
    },
    accountSection: {
      title: 'Account',
    },
    manageAccount: {
      label: 'Manage account',
      description: 'Account options, including deleting your account.',
    },
    error: {
      save: "We couldn't save your privacy preferences. Please try again.",
    },
  },
  notificationsScreen: {
    title: 'Notifications',
    pushSection: {
      title: 'Push notifications',
    },
    pushNotifications: {
      label: 'Push notifications',
      description: 'Get notified on your phone about activity from friends.',
    },
    friendRequests: {
      label: 'Friend requests',
      description: 'When someone sends you a friend request or accepts yours.',
    },
    blockedBySystem:
      'Notifications for Kiroku are turned off in your phone settings.',
    openSettings: 'Open settings',
    mobileOnly: 'Notifications are sent to the Kiroku mobile app.',
  },
  pushNotificationPrompt: {
    title: 'Turn on notifications?',
    prompt:
      "We'll let you know when someone accepts your friend request or sends you one. You can change this in Settings at any time.",
    confirm: 'Turn on',
    cancel: 'Not now',
  },
  blockedUsersScreen: {
    title: 'Blocked users',
    unblockNote:
      'Unblocking does not add someone back as a friend. To be friends again, one of you needs to send a new friend request.',
    unblock: 'Unblock',
    unknownUser: 'Blocked user',
    emptyList: {
      title: "You haven't blocked anyone yet",
      subtitle:
        'When you block someone, they show up here so you can unblock them later.',
    },
  },
  unitsToColorsScreen: {
    title: 'Units to Colors',
    description:
      'Set the maximum units for a Light and a Moderate session. Anything above becomes Heavy.',
  },
  colorPaletteScreen: {
    title: 'Session colors',
    description:
      'Choose a color palette for your session indicators across the calendar and session screens.',
    palettes: {
      classic: 'Classic',
      sunset: 'Sunset',
      ocean: 'Ocean',
      mono: 'Monochrome',
      colorblindSafe: 'Colorblind-safe',
      brand: 'Brand',
      pink: 'Pink',
    },
    descriptions: {
      classic: 'Bright traffic-light hues',
      sunset: 'Warm earth tones',
      ocean: 'Calm blues and teals',
      mono: 'Shades of grey only',
      colorblindSafe: 'Distinguishable for colorblind users',
      brand: "Kiroku's brand colors",
      pink: 'Soft pinks to red',
    },
    useOwnPaletteForOthers: {
      label: 'Use my palette for everyone',
      description:
        "Friends' sessions will appear in your selected palette instead of theirs.",
    },
    custom: {
      label: 'Custom',
      description: 'Pick your own color for each session level.',
      createYourOwn: 'Create your own',
      select: 'Use custom palette',
      edit: 'Edit custom palette',
    },
    editor: {
      title: 'Custom palette',
      selectBand: 'Pick a level, then choose its color.',
      save: 'Save palette',
    },
    bands: {
      green: 'No drinks',
      yellow: 'Light',
      orange: 'Moderate',
      red: 'Heavy',
      black: 'Blackout',
    },
    hex: {
      label: 'Hex color',
      placeholder: '#RRGGBB',
      invalid: 'Enter a valid hex color, like #1A2B3C.',
    },
  },
  drinksToUnitsScreen: {
    title: 'Drinks to Units',
    description: 'Choose how many units each drink is worth',
  },
  languageScreen: {
    language: 'Language',
    languages: {
      en: {
        label: 'English',
      },
      cs_cz: {
        label: 'Czech',
      },
    },
  },
  themeScreen: {
    theme: 'Theme',
    themes: {
      dark: {
        label: 'Dark',
      },
      light: {
        label: 'Light',
      },
      system: {
        label: 'System',
      },
    },
    loading: 'Setting your theme. Please wait...',
    chooseThemeBelowOrSync:
      'Choose a theme below, or sync with your device settings.',
  },
  adminScreen: {
    title: 'Admin Tools',
    generalSection: 'General',
    seeFeedback: 'See feedback',
    seeBugReports: 'See bug reports',
    feedback: 'Feedback',
    bugReports: 'Bug reports',
  },
  sessionSummaryScreen: {
    title: 'Session Summary',
    generalSection: {
      title: 'General',
      sessionColor: 'Session color',
      name: 'Name',
      units: 'Units',
      date: 'Date',
      type: 'Session type',
      startTime: 'Start time',
      lastDrinkAdded: 'Last drink added',
      endTime: 'End time',
    },
    statsSection: {
      duration: 'Duration',
      drinks: 'Drinks',
    },
    drinksSection: {
      title: 'Drinks consumed',
    },
    detailsSection: {
      title: 'Details',
    },
  },
  appShareScreen: {
    title: 'Share the App',
    sectionTitle: 'Everyone, get in here!',
    prompt:
      'Help us grow by sharing Kiroku with your friends! You do so through a link or by scanning a QR code.',
    link: 'Copy share link to clipboard',
    linkCopied: 'Share link copied to clipboard!',
    qrCode: 'Show QR Code',
    error: {
      copy: 'Error copying to clipboard. Please try again.',
    },
  },
  settingsScreen: {
    title: 'Settings',
    deleteAccount: 'Delete account',
    improvementThoughts: 'What would you like us to improve?',
    general: 'General',
    reportBug: 'Report a bug',
    giveFeedback: 'Give us feedback',
    help: 'Help & support',
    signOut: 'Sign out',
    shareTheApp: 'Share the app',
    adminTools: 'Admin tools',
    about: 'About',
    // signOutConfirmationText: "You'll lose any offline changes if you sign out.",
    signOutConfirmationText: 'Do you really want to sign out?',
    signingOut: 'Signing out...',
    aboutScreen: {
      viewTheCode: 'View the code',
      aboutKiroku: 'About Kiroku',
      description:
        'Kiroku is a mobile app that helps you track your alcohol consumption.',
      joinDiscord: 'Join our Discord server',
      versionLetter: 'v',
      readTheTermsAndPrivacy: {
        phrase1: 'Read the',
        phrase2: 'Terms of Service',
        phrase3: 'and',
        phrase4: 'Privacy Policy',
      },
    },
    termsOfServiceScreen: {
      loading: 'Loading Terms of Service...',
    },
    privacyPolicyScreen: {
      loading: 'Loading Privacy Policy...',
    },
    subscriptionTermsScreen: {
      loading: 'Loading Subscription Terms...',
    },
    helpScreen: {
      loading: 'Loading support...',
    },
    error: {},
  },
  supporter: {
    tierName: 'Kiroku Supporter',
    badgeAccessibilityLabel: 'Kiroku Supporter',
    benefit: 'Supporter badge on your profile',
    description:
      'Support Kiroku and get the 🍺 supporter badge on your profile.',
    menuEntry: 'Support Kiroku',
    tipJar: {
      title: 'Tip jar',
      subtitle:
        'Kiroku is free and has no ads. To support the app development, you can buy us a beer.',
      heroTitle: 'Buy us a beer',
      tierSmallBeerShort: 'Small beer',
      tierBeerShort: 'Beer',
      tierRoundShort: 'A round',
      mostPopular: 'Popular',
      ctaSmallBeer: ({price}: SupporterPriceParams) =>
        `Buy a small beer · ${price}`,
      ctaBeer: ({price}: SupporterPriceParams) => `Buy a beer · ${price}`,
      ctaRound: ({price}: SupporterPriceParams) => `Buy a round · ${price}`,
      thanksCount: ({count}: SupporterTipCountParams) =>
        count === 1 ? 'You bought us a beer' : `You bought us ${count} beers`,
      unlocksNothing:
        'Tips are optional and unlock nothing. The app stays the same for everyone.',
      thanks: 'Thank you for the beer. Now go and have one yourself!',
      cheersTitle: 'Cheers.',
      receiptNote: 'More beers for us, more features for you!',
      loading: 'Reaching the tip jar...',
      unavailable:
        "The tip jar can't be reached right now. Check your connection and try again.",
      webUnavailable: 'Tips are available in the mobile app.',
      purchaseError: ({message}: SupporterPurchaseErrorParams) =>
        `Purchase failed: ${message}. Please try again.`,
    },
    tipJarPrompt: {
      title: ({sessionCount}: TipJarPromptTitleParams) =>
        `${sessionCount} sessions logged. Fancy buying us a beer?`,
      body: 'Kiroku is free and has no ads. Tips unlock nothing.',
      accept: 'Buy us a beer',
      notNow: 'Not now',
      never: "Don't ask again",
    },
    paywallScreen: {
      title: 'Support Kiroku',
      heroPill: 'Supporter badge',
      heroTitle: 'Become a Kiroku Supporter',
      heroSubtitle:
        'Show your support and wear the exclusive supporter badge on your profile.',
      featureBadgeTitle: 'Supporter Badge',
      featureBadgeDescription:
        'Exclusive badge displayed on your public profile',
      featureSupportTitle: 'Support Development',
      featureSupportDescription: 'Help keep Kiroku free and actively developed',
      featureEarlyAccessTitle: 'Early Access',
      featureEarlyAccessDescription:
        'First to know about new features and updates',
      planAnnualTitle: 'Annual',
      planMonthlyTitle: 'Monthly',
      planMonthlyBilling: 'Billed every month',
      bestValue: 'Best value',
      pricePerYearShort: ({price}: SupporterPriceParams) => `${price} / yr`,
      pricePerMonthShort: ({price}: SupporterPriceParams) => `${price} / mo`,
      pricePerYear: ({price}: SupporterPriceParams) => `${price} / year`,
      pricePerMonth: ({price}: SupporterPriceParams) => `${price} / month`,
      startSupportingCta: ({price}: SupporterPriceParams) =>
        `Start supporting for ${price}`,
      loading: 'Loading subscription details...',
      thanksTitle: "You're a Kiroku Supporter",
      thanksSubtitle:
        'Thanks for backing the app. Your supporter badge is live on your profile.',
      unavailableTitle: 'Subscription unavailable',
      unavailableSubtitle:
        "We couldn't load the supporter subscription right now. Check your connection and try again.",
      purchaseCta: ({price}: SupporterPurchaseCtaParams) =>
        `Become a supporter for ${price} / month`,
      purchaseError: ({message}: SupporterPurchaseErrorParams) =>
        `Purchase failed: ${message}. Please try again.`,
      restoreError: ({message}: SupporterPurchaseErrorParams) =>
        `Restore failed: ${message}. Please try again.`,
      restorePurchases: 'Restore purchases',
      restoreEmpty:
        'No prior supporter subscription found on this account. If you purchased on another device, sign in with the same account and try again.',
      autoRenewalNotice:
        'Subscription auto-renews monthly until cancelled. Manage or cancel anytime in the App Store or Google Play at least 24 hours before the renewal date.',
      retry: 'Try again',
      manageSubscriptionLink: 'Manage subscription',
    },
    manageSubscription: {
      title: 'Manage Subscription',
      statusHeader: 'Subscription status',
      status: {
        active: 'Active',
        cancelled: ({date}: SupporterCancelledStatusParams) =>
          `Cancelled, active until ${date}`,
        gracePeriod: 'Payment issue',
        expired: 'Expired',
      },
      renewsOn: ({date}: SupporterRenewalDateParams) => `Renews on ${date}`,
      expiredOn: ({date}: SupporterRenewalDateParams) => `Expired on ${date}`,
      manageInAppStore: 'Manage in App Store',
      manageInGooglePlay: 'Manage in Google Play',
      restorePurchases: 'Restore purchases',
      restoreSuccess: 'Purchases restored. Your supporter status is active.',
      restoreError: ({message}: SupporterPurchaseErrorParams) =>
        `Restore failed: ${message}. Please try again.`,
      restoreEmpty:
        'No active supporter subscription was found on this account.',
      billingIssueCopy:
        "Your payment couldn't be processed. Update your payment method to keep your supporter status.",
      expiredCopy:
        'Your supporter subscription has expired. Subscribe again from the Support Kiroku screen.',
    },
  },
  premiumFeatures: {
    plusBadge: 'Plus',
    upsellAccessibilityLabel: 'Kiroku Plus feature. Tap to learn more.',
  },
  accountScreen: {
    title: 'Profile Details',
    generalOptions: {
      title: 'General',
    },
    personalDetails: {
      title: 'Personal Details',
      subtitle:
        'These details help us provide you with the best user experience.',
    },
  },
  drinkingSession: {
    type: {
      live: 'Live',
      edit: 'Edit',
    },
    live: {
      title: 'Live',
      description: 'Track drinks as you go',
    },
    edit: {
      title: 'Edit',
      description: 'Log past sessions',
    },
    error: {
      sessionOpen: 'Failed to open a session',
      missingId: 'Missing session ID',
      missingData: 'Missing session data',
    },
    // The name a new session gets before the user renames it: the weekday and
    // the part of the day it started in, e.g. "Friday evening".
    defaultName: ({weekday, partOfDay}: SessionDefaultNameParams) =>
      `${weekday} ${partOfDay}`,
    partOfDay: {
      morning: 'morning',
      afternoon: 'afternoon',
      evening: 'evening',
      night: 'night',
    },
  },
  startSession: {
    ongoingSessions: 'Ongoing sessions',
    startNewSession: 'Start a new session',
    newSession: 'Start a session',
    newSessionExplained: 'Start a session (Floating action)',
    sessionFrom: ({startTime}: SessionStartTimeParams) =>
      `A session from ${startTime}`,
    unavailableTitle: 'Sessions not loaded',
    unavailableMessage:
      'We could not load your sessions. Reconnect to the internet, then try again.',
  },
  userNameScreen: {
    headerTitle: 'User name',
    explanation:
      'Displaying your name helps your friends easily find and recognize you on your profile.',
    note: "Note: Your name will not show up elsewhere in the app just yet. We're working on it!",
    updatingUserName: 'Updating your name...',
  },
  displayNameScreen: {
    headerTitle: 'Nickname',
    isShownOnProfile: 'Your nickname is shown on your profile.',
    updatingDisplayName: 'Updating your nickname...',
  },
  onboarding: {
    title: 'Onboarding',
    stepCounter: ({currentStep, totalSteps}: OnboardingStepCounterParams) =>
      `Step ${currentStep} of ${totalSteps}`,
    terms: {
      heading: 'Please read our terms of service',
      description:
        'Please read the terms of service and privacy policy before continuing.',
    },
  },
  timezoneScreen: {
    timezone: 'Timezone',
    isShownOnProfile: 'Your timezone is shown on your profile.',
    getLocationAutomatically: 'Automatically determine your location',
    saving: 'Saving your timezone...',
  },
  emailScreen: {
    title: 'Update Email',
    prompt: 'Your email is used to log in and receive important notifications.',
    note: "Note: Upon confirmation, we will send a verification email to your new address. You'll need to verify it to complete the change, and restart the app afterwards to see changes.",
    enterEmail: 'Your email address',
    submit: 'Update email',
    sent: 'Email updated successfully!',
    sending: 'Updating email...',
    success: ({email}: UpdateEmailSentEmailParams) =>
      `An email with instructions to change your email address has been sent to ${email}. Please reload the app after changing your email.`,
    enterPasswordToConfirm:
      'Please enter your password to confirm your identity.',
    enterPassword: 'Enter your password',
  },
  verifyEmailScreen: {
    title: 'Verify your email',
    body: ({email}: VerifyEmailScreenEmailParmas) =>
      `We sent a verification link to ${email ?? 'your email'}. Open it to confirm your address, then come back here. Don't see it? Check your spam folder.`,
    resendEmail: 'Resend email',
    emailSent: 'Verification email sent.',
    emailVerified: 'Email verified!',
    iHaveVerified: 'I have verified my email',
    useADifferentEmail: 'Wrong email? Use a different one',
    skipVerificationDevOnly: 'Skip verification (dev only)',
    changeEmail: {
      title: 'Use a different email',
      prompt:
        "Enter a new address and your password. We'll send the verification link there instead.",
      newEmailLabel: 'New email address',
      passwordLabel: 'Your password',
      submit: 'Send verification link',
      sent: ({email}: VerifyEmailScreenEmailParmas) =>
        `Verification link sent to ${email}. Open it, then tap 'I have verified my email'.`,
      back: 'Back',
      error: {
        passwordRequired: 'Please enter your password.',
      },
    },
    error: {
      generic: 'Error verifying your email',
      sending: 'Error sending verification email',
      emailSentRecently:
        'Please wait before sending another verification email.',
      emailNotVerified: 'Your email has not been verified yet.',
    },
  },
  reportBugScreen: {
    title: 'Report a bug',
    prompt: 'What happened? Please describe the bug in detail.',
    describeBug: 'Describe the bug here',
    submit: 'Submit bug report',
    sent: 'Bug report sent!',
    sending: 'Sending a bug report...',
    error: 'There was bug reporting your bug. Please try again.',
  },
  feedbackScreen: {
    title: 'Feedback',
    prompt: 'What would you like us to improve?',
    enterFeedback: 'Enter your feedback here',
    submit: 'Submit feedback',
    sent: 'Feedback sent!',
    sending: 'Sending feedback...',
    error: 'There was an error sending your feedback. Please try again.',
  },
  accountSuspendedScreen: {
    title: 'Account suspended',
    body: 'Your account has been suspended for violating our community guidelines. If you think this is a mistake, contact support.',
    contactSupport: 'Contact support',
  },
  reportUserScreen: {
    title: 'Report',
    prompt:
      'Why are you reporting this user? Your report is private and goes to our moderation team.',
    photoPrompt:
      'Why are you reporting this photo? Your report is private and goes to our moderation team.',
    reasons: {
      inappropriateName: 'Inappropriate name',
      inappropriatePhoto: 'Inappropriate profile photo',
      sessionPhoto: 'Inappropriate session photo',
      harassment: 'Harassment or bullying',
      other: 'Something else',
    },
    descriptionLabel: 'Add details (optional)',
    alsoBlock: 'Also block this user',
    submit: 'Submit report',
    successTitle: 'Report received',
    successMessage:
      'Thanks for letting us know. Our moderation team will review this user.',
  },
  manageAccountScreen: {
    title: 'Manage account',
    dangerZone: {
      title: 'Danger zone',
      subtitle: 'These actions are permanent and cannot be undone.',
    },
    deleteAccount: {
      title: 'Delete account',
    },
  },
  deleteAccountScreen: {
    deleteAccount: 'Delete account',
    reasonForLeavingPrompt:
      'We’d hate to see you go! Would you kindly tell us why, so we can improve?',
    enterMessageHere: 'Enter message here',
    deleteAccountWarning: 'Deleting your account cannot be undone.',
    deleteAccountPermanentlyDeleteData:
      'Are you sure you want to delete your account? This will permanently delete all of your data.',
    enterPasswordToConfirm: 'Please enter your password to confirm deletion.',
    enterPassword: 'Enter your password',
    deletingAccount: 'Deleting account...',
    confirmWithProviderPrompt: ({provider}: ConfirmWithProviderPromptParams) =>
      `To confirm deletion, you'll be asked to sign in with ${provider}.`,
    error: {
      unsupportedProvider:
        'Your sign-in method is not supported for account deletion. Please contact support.',
    },
  },
  profileScreen: {
    title: 'Profile',
    titleNotSelf: 'Friend Overview',
    seeAllFriends: 'See all friends',
    drinkingSessions: ({sessionsCount}: DrinkingSessionsParams) =>
      `Drinking ${Str.pluralize('Session', 'Sessions', sessionsCount)}`,
    unitsConsumed: ({unitCount}: UnitCountParams) =>
      `${Str.pluralize('Unit', 'Units', unitCount)} Consumed`,
    manageFriend: 'Manage Friend',
    report: 'Report',
    unfriendPrompt: 'Do you really want to unfriend this user?',
    unfriend: 'Unfriend',
    blockUser: 'Block user',
    blockUserTitle: 'Block this user?',
    blockUserPrompt:
      'Blocking removes your friendship and hides each of you from the other. They will not be able to find you or send you a friend request. You can unblock them later.',
    hideDataFromFriend: 'Hide my data from this friend',
    showDataToFriend: 'Show my data to this friend',
    commonFriendsLabel: ({hasCommonFriends}: CommonFriendsLabelParams) =>
      `${hasCommonFriends ? 'Common friends:' : 'Friends:'}`,
    profileImage: 'Profile Image',
    offlineUnavailableTitle: "Couldn't load this profile",
    offlineUnavailableMessage:
      "We couldn't load this profile while offline. Reconnect to view it.",
    privateTitle: 'Private profile',
    privateMessage: 'This person keeps their drinking sessions private.',
  },
  statistics: {
    title: 'Statistics',
    period: {
      weekOf: ({date}: WeekOfParams) => `Week of ${date}`,
    },
    tabs: {
      overview: {
        label: 'Overview',
        hero: {
          label: 'Units this period',
          unit: 'units',
        },
        delta: {
          vsPrevious: 'vs previous',
        },
        sections: {
          highlights: 'Highlights',
          consumption: 'Consumption',
          heavyDays: 'Heavy days',
        },
        kpi: {
          afDays: {label: 'Alcohol-free days'},
          dryStreak: {label: 'Longest dry streak', unit: 'days'},
          sessions: {label: 'Sessions'},
          heaviestDay: {label: 'Heaviest day', unit: 'units'},
          avgPerDrinkingDay: {label: 'Avg / drinking day', unit: 'units'},
          monthlyAvg: {label: 'Avg / month', unit: 'units'},
          daysOverYellow: {
            label: ({threshold}: StatsThresholdParams) =>
              `Days over ${threshold}`,
          },
          daysOverOrange: {
            label: ({threshold}: StatsThresholdParams) =>
              `Days over ${threshold}`,
          },
          pctOver: {
            label: ({threshold}: StatsThresholdParams) =>
              `% days over ${threshold}`,
          },
        },
        texture: {
          series: {
            title: 'Units by period',
            a11y: 'Units per sub-period',
          },
          distribution: {
            title: 'Days by intensity',
            a11y: 'Distribution of days by drinking intensity',
            af: 'AF',
            light: 'Light',
            moderate: 'Moderate',
            heavy: 'Heavy',
          },
        },
        empty: {
          neverLogged: {
            title: 'Nothing to log yet',
            body: 'When you log a session, your period scorecard will show up here.',
          },
          noDataInRange:
            'No sessions in this period. Every elapsed day was alcohol-free.',
        },
        sparseFooter:
          'Only a little history so far. These numbers will sharpen as you log more.',
      },
      trends: {
        label: 'Trends',
        weeklyTrend: {
          title: 'Weekly units',
          emptyLabel: 'Your trend will show as data builds.',
          legend: {
            perWeek: 'Per week',
            trend: 'Trend',
          },
          captions: {
            trendingDown: 'Your weekly units are trending down.',
            trendingUp: 'Your weekly units are trending up.',
            neutral: 'Your weeks vary as usual.',
            notEnoughData:
              'Keep logging. Your trend will surface as data builds.',
          },
        },
        afRate: {
          title: 'Alcohol-free rate',
          emptyLabel: 'Your alcohol-free rate will show as data builds.',
        },
        drinkTypeStack: {
          title: 'Drink mix over time',
          emptyLabel: 'Your drink mix will appear as you log more.',
        },
        comparison: {
          legend: 'Previous period',
        },
      },
      patterns: {
        label: 'Patterns',
        placeholder:
          'Your when-and-how (hour of day and day of week) will live here.',
      },
      breakdown: {
        label: 'Breakdown',
        donut: {
          title: 'Units by drink type',
          subtitle: 'Composition over the current range.',
          centerUnits: ({count}: BreakdownCenterUnitsParams) => `${count}`,
          centerCaption: 'units',
          empty: 'Nothing to break down yet.',
          sliceCaption: ({label, units, share}: BreakdownSliceCaptionParams) =>
            `${label}: ${units} units (${share}%)`,
          a11y: 'Drink-type composition donut',
          current: 'Current',
          previous: 'Previous',
        },
        multiples: {
          title: 'Weekly trend by type',
          subtitle: 'One mini chart per drink type with data in this range.',
          empty: 'No per-type trends yet. Try a wider range.',
          tileSubtitle: ({units}: BreakdownTileSubtitleParams) =>
            `${units} units this range`,
          a11yTile: ({label}: BreakdownDrinkLabelParams) =>
            `Weekly trend for ${label}`,
        },
        concentration: {
          moreVaried: ({period}: BreakdownPeriodParams) =>
            `You've been more varied this ${period} than last.`,
          moreFocused: ({period}: BreakdownPeriodParams) =>
            `You've been more focused this ${period} than last.`,
          aboutTheSame: ({period}: BreakdownPeriodParams) =>
            `Your mix of drinks is about the same as last ${period}.`,
          period: {
            week: 'week',
            month: 'month',
            sixMonths: '6 months',
            year: 'year',
            window: 'window',
          },
        },
      },
    },
    legend: {
      drinkTypeA11y: 'Drink type legend',
    },
    charts: {
      weeklyBars: {
        title: 'Last 8 weeks',
      },
      calendarHeatmap: {
        title: 'This month',
      },
      hourOfDay: {
        title: 'Hour of day',
        empty: 'Nothing to plot here.',
      },
      dowHour: {
        title: 'Day of week × hour',
        empty: 'Nothing to plot here.',
      },
      drinksPerSession: {
        title: 'Drinks per session',
        empty: 'No sessions to count.',
        p75Copy: ({value}: {value: number}) =>
          `75% of your sessions are ${value} drinks or fewer.`,
      },
      sessionDuration: {
        title: 'Session length',
        empty: 'No sessions to measure.',
        p75Copy: ({value}: {value: string}) =>
          `75% of your sessions are ${value} or shorter.`,
      },
    },
    drilldown: {
      empty: 'No sessions match this view.',
      close: 'Close',
      title: {
        day: ({label}: StatsDrillDownTitleParams) => `Sessions on ${label}`,
        isoWeek: ({label}: StatsDrillDownTitleParams) => `Week ${label}`,
        month: ({label}: StatsDrillDownTitleParams) => `Sessions in ${label}`,
        hour: ({label}: StatsDrillDownTitleParams) =>
          `Sessions around ${label}`,
        dow: ({label}: StatsDrillDownTitleParams) => `${label} sessions`,
        dowHour: ({label}: StatsDrillDownTitleParams) => label,
        drinkType: ({label}: StatsDrillDownTitleParams) => `${label} sessions`,
        isoWeekDrinkType: ({label}: StatsDrillDownTitleParams) => label,
        sessionDrinkCountBin: ({label}: StatsDrillDownTitleParams) =>
          `Sessions with ${label}`,
        sessionDurationBin: ({label}: StatsDrillDownTitleParams) =>
          `Sessions lasting ${label}`,
      },
    },
    filters: {
      range: {
        W: 'W',
        M: 'M',
        sixM: '6M',
        Y: 'Y',
        all: 'All',
        custom: 'Custom',
      },
      drinkType: {
        all: 'All drinks',
      },
      sessionType: {
        liveOnly: 'Live only',
      },
      comparison: {
        none: 'Compare',
        previousPeriod: 'vs last period',
        previousYear: 'vs last year',
      },
      label: {
        thisWeek: 'This Week',
        thisMonth: 'This Month',
        thisYear: 'This Year',
        allTime: 'All time',
        jumpToLatest: 'Jump to latest',
      },
      customRange: {
        title: 'Pick a date range',
        apply: 'Apply',
        cancel: 'Cancel',
      },
      a11y: {
        rangeSegmentedControl: 'Date range selector',
        drinkTypeChipRow: 'Drink type filter',
        sessionTypeToggle: 'Show live sessions only',
        comparisonToggle: 'Comparison mode',
        previousPeriod: 'Previous period',
        nextPeriod: 'Next period',
        rangeLabel: 'Selected date range, double-tap to change',
        jumpToLatest: 'Jump to the latest period',
        revertToPreset: 'Revert to the previous range',
      },
    },
  },
  badgesScreen: {
    title: 'Badges',
    badgesTitle: 'Badges',
    empty:
      'Log your first session to start earning badges and tracking your alcohol-free streak.',
    dayUnit: ({count}: BadgesDayCountParams) => (count === 1 ? 'day' : 'days'),
    streak: {
      label: 'Current alcohol-free streak',
    },
    stats: {
      longestStreak: 'Longest streak',
      totalAfDays: 'Alcohol-free days',
      sessions: 'Sessions logged',
    },
    badges: {
      firstSession: {
        title: 'First session',
        description: 'Log your first drinking session.',
      },
      dryDay: {
        title: 'First dry day',
        description: 'Record your first alcohol-free day.',
      },
      dryWeek: {
        title: 'Dry week',
        description: 'Reach a 7-day alcohol-free streak.',
      },
      dryFortnight: {
        title: 'Dry fortnight',
        description: 'Reach a 14-day alcohol-free streak.',
      },
      dryMonth: {
        title: 'Dry month',
        description: 'Reach a 30-day alcohol-free streak.',
      },
      afDays10: {
        title: '10 dry days',
        description: 'Add up 10 alcohol-free days.',
      },
      afDays50: {
        title: '50 dry days',
        description: 'Add up 50 alcohol-free days.',
      },
      sessions25: {
        title: 'Diligent logger',
        description: 'Log 25 drinking sessions.',
      },
    },
  },
  dayOverviewScreen: {
    enterEditMode: 'Edit Mode',
    exitEditMode: 'Exit Edit Mode',
    noDrinkingSessions: 'No drinking sessions',
    addSessionExplained: 'Add a session (Floating action)',
    selectSessionDate: 'Select a date for the new session',
    sessionWindow: ({sessionId}: SessionWindowIdParams) =>
      `Drinking session: ${sessionId}`,
    ongoing: 'Ongoing',
    loadingDate: 'Loading date...',
    error: {
      open: 'Failed to open a new session. Please try again.',
    },
  },
  homeScreen: {
    startingSession: 'Starting a new session...',
    welcomeToKiroku: 'Welcome to Kiroku!',
    startNewSessionByClickingPlus:
      'Start a new session by clicking the plus button at the bottom of your screen',
    offlineNoData: {
      title: "You're offline",
      message:
        "We couldn't load your sessions. Reconnect and they'll show up here.",
    },
    liveSessionCard: {
      label: 'Live session',
      open: 'Open',
      // Units can be fractional ("0.5 units"), so only exactly 1 is singular.
      units: ({unitCount}: UnitCountParams) =>
        `${unitCount} ${unitCount === 1 ? 'unit' : 'units'}`,
      a11y: ({unitCount}: UnitCountParams) =>
        `Open your live session, ${unitCount} ${unitCount === 1 ? 'unit' : 'units'} so far`,
      addDrink: ({drinkName}: QuickAddDrinkParams) => `Add one: ${drinkName}`,
    },
    banners: {
      lastSession: {
        label: 'Last session',
        summary: ({when, units}: LastSessionSummaryParams) =>
          `${when} · ${units} units`,
        a11y: ({when, units}: LastSessionSummaryParams) =>
          `View your last session, ${when}, ${units} units`,
        today: 'Today',
        yesterday: 'Yesterday',
        daysAgo: ({count}: RelativeTimeAgoParams) =>
          `${count} ${Str.pluralize('day', 'days', count)} ago`,
        monthsAgo: ({count}: RelativeTimeAgoParams) =>
          `${count} ${Str.pluralize('month', 'months', count)} ago`,
        yearsAgo: ({count}: RelativeTimeAgoParams) =>
          `${count} ${Str.pluralize('year', 'years', count)} ago`,
      },
    },
    stats: {
      thisMonth: 'This month',
      noChange: 'No change',
      units: 'Units',
      sessions: 'Sessions',
      alcoholFree: 'Alcohol-free',
      statistics: 'Statistics',
      viewStatistics: 'View statistics',
      vsLastMonth: 'vs last month',
      monthToDate: 'to date',
      unitsByWeek: 'Units by week',
      unitsPerWeekA11y: 'Units consumed per week this month',
    },
  },
  sessionsCalendar: {
    dayOverviewButton: "Day's overview",
    jumpToCurrentMonth: 'Jump to the current month',
  },
  liveSessionScreen: {
    saving: 'Saving your session...',
    ending: 'Ending your session...',
    synchronizing: 'Synchronizing data...',
    loading: 'Loading your session...',
    drinksConsumed: 'Drinks consumed',
    sessionFrom: 'Session from',
    sessionOn: 'Session on',
    blackout: 'Blackout',
    blackoutSwitchLabel:
      'This indicates whether your session ended in a blackout.',
    note: 'Note',
    name: 'Name',
    private: 'Private',
    privateDescription: 'Only you can see this session.',
    privateSwitchLabel:
      'When this is on, the session stays hidden from your friends.',
    discardSessionWarning: (discardWord: string) =>
      `Do you really want to ${discardWord} this session?`,
    unsavedChangesWarning:
      'You have unsaved changes. Are you sure you want to go back?',
    sessionDetails: 'Session details',
    discardSession: ({discardWord}: DiscardSessionParams) =>
      `${discardWord} Session`,
    saveSession: 'Save Session',
    endSession: 'End session',
    // Live sessions persist every drink as it's logged, so "End session" only
    // stamps the end time. This line pre-empts "do I need to save?".
    drinksAutoSaved: 'Drinks are saved as you add them.',
    discardingSession: ({discardWord}: DiscardSessionParams) =>
      `${discardWord} this session...`,
  },
  sessionDateScreen: {
    title: 'Session date',
    prompt: 'Please choose a date when you started this session.',
    error: {
      load: 'Failed to fetch details of this session.',
      generic: 'Failed to modify the session date.',
    },
  },
  sessionNameScreen: {
    title: 'Session name',
    nameDescription:
      'Name this session so you can tell it apart later. Friends who can see the session see the name too.',
    error: {
      nameRequired: 'Please give the session a name.',
    },
  },
  sessionNoteScreen: {
    title: 'Session note',
    noteDescription: "This note is private and won't be shared with others.",
    error: {
      load: 'Failed to fetch details of this session.',
      generic: 'Failed to modify the session note.',
      noteTooLongError: 'Your note is too long.',
    },
  },
  sessionTimezoneScreen: {
    title: 'Session timezone',
    description:
      'Please choose the timezone you were in when you started the session.',
    note: 'Note: Each time the details of this session are viewed, its timestamps will be displayed in the selected timezone.',
    confirmPrompt: ({newTimezone}: SessionConfirmTimezoneChangeParams) =>
      `Setting the timezone to ${newTimezone} will change the date of this session. Are you sure you'd like to proceed?`,
    error: {
      generic: 'Failed to modify the session timezone.',
      errorSelectTimezone: 'Failed to select a timezone. Please try again.',
    },
  },
  maintenance: {
    heading: 'Under Maintenance',
    text: 'We are currently under maintenance for the following time frame:',
  },
  testTools: {
    title: 'Test Tools',
    intro: 'Developer-only panel for runtime tweaks and debugging.',
    placeholderNotice:
      'This is a placeholder. Real toggles (feature flags, environment overrides, forced network states) will land here over time.',
    simulatePlus: 'Simulate Plus',
    simulatePlusDescription:
      'Treat this account as a Kiroku Plus supporter so gated features unlock.',
    featureOverridesTitle: 'Premium feature overrides',
    override: {
      auto: 'Auto',
      locked: 'Locked',
      unlocked: 'Unlocked',
    },
    resetOverrides: 'Reset overrides',
    tipJarPrompt: {
      title: 'Tip jar card',
      description:
        'The one ask on Home. Normally it needs 14 days on this device, 5 sessions, no tip given here, and a session summary closing first.',
      status: ({
        firstOpen,
        shownCount,
        tipsGiven,
      }: TipJarPromptDevStatusParams) =>
        `First open: ${firstOpen ?? 'not yet'} · Shown: ${shownCount} · Tips on this device: ${tipsGiven}`,
      dismissedForever: 'Dismissed for good on this device.',
      showNow: 'Show the card now',
      makeEligible: 'Make eligible (then close a session summary)',
      reset: 'Reset card state',
    },
    environmentLabel: 'Environment',
    howToOpen:
      'Open via ⌘D → Open Test Preferences, or a four-finger tap anywhere in the app.',
  },
  userList: {
    noFriendsFound: 'No friends found.',
    tryModifyingSearch: 'Try modifying the search text.',
  },
  userOverview: {
    inSession: 'In session',
    from: 'From',
    // Describes the logged data ("last session was 3h ago"), never the person's
    // bodily state. Wording that reads as a sobriety readout about a user is a
    // App Store Guideline 1.4 hook for an alcohol app; keep it data-facing.
    lastSession: 'Last session',
    sessionStarted: 'Session started',
    noSessionsYet: 'No sessions yet',
    private: 'Private',
  },
  yearPickerScreen: {
    year: 'Year',
    selectYear: 'Please select a year',
  },
  forceUpdate: {
    heading: 'App Update Required',
    text: 'This version of the app is no longer supported. Update to the latest version using the link below.',
    link: 'Update Now',
  },
  login: {
    hero: {
      header: 'Keep track of your alcohol adventures',
      body: 'Log your drinks and share them with your friends.',
    },
    email: 'Email',
    existingAccount: 'Already have an account?',
    noAccount: 'Don’t have an account yet?',
  },
  password: {
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    reEnter: 'Re-enter your password',
    requirements:
      'Your password must contain at least 8 characters, 1 capital letter, 1 lowercase letter, and 1 number.',
    pleaseFillOutAllFields: 'Please fill out all fields',
    pleaseFillPassword: 'Please enter your password',
    forgot: 'Forgot password?',
    changingPassword: 'Changing your password...',
    error: {
      samePassword: 'This is the same password as your current one',
      incorrectPassword: 'Incorrect password. Please try again.',
      incorrectLoginOrPassword:
        'Incorrect login or password. Please try again.',
      invalidLoginOrPassword:
        'Invalid login or password. Please try again or reset your password.',
      unableToResetPassword:
        'We were unable to change your password. This is likely due to an expired password reset link in an old password reset email. We have emailed you a new link so you can try again. Check your Inbox and your Spam folder; it should arrive in just a few minutes.',
      accountLocked:
        'Your account has been locked after too many unsuccessful attempts. Please try again after 1 hour.',
      fallback: 'Something went wrong. Please try again later.',
      passwordsMustMatch: 'The passwords do not match.',
    },
  },
  baseUpdateAppModal: {
    updateApp: 'Update app',
    updatePrompt:
      'A new version of this app is available.\nWould you like to update now?',
  },
  username: {
    error: {
      usernameRequired: 'A username is required',
      usernameTooLong: 'This username is too long',
      sameUsername: 'This is the same username as your current one',
    },
  },
  pickUsernameScreen: {
    heading: 'Choose a username',
    explainer: 'Pick a username. This is how others will see you.',
    saving: 'Saving...',
    error: {
      generic: 'Could not save your username. Please try again.',
    },
  },
  emailForm: {
    email: 'Email',
    error: {
      invalidEmail: 'Invalid email address',
      sameEmail: 'This is the same email address as your current one',
      emailTooLong: 'The email address is too long',
      emailRequired: 'An email address is required',
      pleaseEnterEmail: 'Please enter an email address',
      generic: 'There was an error updating your email address.',
    },
  },
  logInScreen: {
    loggingIn: 'Logging in...',
  },
  signUpScreen: {
    signingIn: 'Signing in...',
    signingYouIn: 'Signing you in...',
  },
  forgotPasswordScreen: {
    title: 'Forgotten password',
    prompt:
      'We will send you instructions on how to reset your password using the following email:',
    sending: 'Sending you an email...',
    submit: 'Reset your password',
    enterEmail: 'Enter your email here',
    success: ({email}: ForgotPasswordSuccessParams) =>
      `An email with password reset instructions has been sent to ${email}. Don't see it? Check your spam folder.`,
    error: {
      generic: 'There was an error when attempting to reset your password.',
    },
  },
  connectedAccounts: {
    title: 'Connected accounts',
    subtitle:
      'Choose how you want to sign in. You can connect more than one. Any of them will sign you into the same account.',
    providers: {
      password: 'Email & password',
      apple: 'Apple',
      google: 'Google',
    },
    status: {
      signedInWith: 'Signed in with this',
      connected: 'Connected',
      notConnected: 'Not connected',
    },
    actions: {
      connect: 'Connect',
      disconnect: 'Disconnect',
      cancel: 'Cancel',
    },
    unlinkConfirm: {
      title: ({provider}: {provider: string}) => `Disconnect ${provider}?`,
      prompt: ({remaining}: {remaining: string}) =>
        `You'll only be able to sign in with ${remaining} from now on.`,
      confirm: 'Disconnect',
    },
    reauth: {
      title: "Confirm it's you",
      passwordPrompt:
        'For your security, please re-enter your password to make this change.',
      oauthPrompt: ({provider}: {provider: string}) =>
        `For your security, please sign in again with ${provider} to make this change.`,
      submit: 'Confirm',
      reauthWith: ({provider}: {provider: string}) =>
        `Continue with ${provider}`,
      error: "We couldn't confirm your identity. Please try again.",
    },
    privateRelay:
      'Apple is hiding your real email with a relay address. If you disconnect and reconnect Apple later, the relay address will be different.',
    success: {
      connected: ({provider}: {provider: string}) =>
        `${provider} is now connected.`,
      disconnected: ({provider}: {provider: string}) =>
        `${provider} has been disconnected.`,
    },
    error: {
      generic: "We couldn't update your connected accounts. Please try again.",
    },
  },
  oauthLinkModal: {
    appleTitle: 'Connect your Apple account',
    googleTitle: 'Connect your Google account',
    body: ({email}: ForgotPasswordSuccessParams) =>
      `An account with ${email} already exists. Enter your password to connect.`,
    appleSubmit: 'Connect Apple account',
    googleSubmit: 'Connect Google account',
    resetEmailSent: ({email}: ForgotPasswordSuccessParams) =>
      `We sent a reset link to ${email}. Check your inbox, and your spam folder if you don't see it.`,
  },
  passwordScreen: {
    title: 'Change your password',
    prompt: 'Please enter your current password and then your new password.',
    submit: 'Change password',
    success: 'Password changed successfully!',
    error: {
      generic: 'There was an error when attempting to change your password.',
    },
  },
  closeAccount: {
    successMessage: 'Your account has been successfully deleted.',
  },
  database: {
    loading: 'Fetching data...',
    error: {
      generic: 'Failed to reach the database',
      userDoesNotExist: 'User does not exist in the database',
      saveData: 'Failed to save your data to the database. Please try again.',
    },
  },
  genericErrorScreen: {
    title: 'Oops, something went wrong!',
    body: {
      helpTextMobile: 'Please close and reopen the app.',
      // helpTextMobile: 'Please close and reopen the app, or switch to',
      // helpTextWeb: 'web.',
      helpTextEmail: 'If the problem persists, reach out to',
    },
    refresh: 'Refresh',
  },
  errors: {
    storage: {
      objectNotFound: {
        title: 'Object Not Found',
        message:
          'The requested object could not be found. Please check the details and try again.',
      },
      unauthorized: {
        title: 'Unauthorized Access',
        message:
          'You do not have the necessary permissions to perform this action.',
      },
    },
    auth: {
      accountDeletionFailed: {
        title: 'Account Deletion Failed',
        message:
          'There was an issue deleting your account. Please try again later.',
      },
      missingEmail: {
        title: 'Missing Email',
        message: 'Please provide a valid email address to continue.',
      },
      invalidEmail: {
        title: 'Invalid Email',
        message:
          'The email address provided is not valid. Please check and try again.',
      },
      verifyEmail: {
        title: 'Email Verification Required',
        message:
          'Please verify your email address before making changes to it.',
      },
      missingPassword: {
        title: 'Missing Password',
        message:
          'A password is required to proceed. Please enter your password.',
      },
      invalidCredential: {
        title: 'Wrong email or password',
        message:
          'Check your email and password. If you originally signed up with Apple or Google, sign in with those instead.',
      },
      weakPassword: {
        title: 'Weak Password',
        message:
          'Your password must be at least 6 characters long. Please choose a stronger password.',
      },
      emailAlreadyInUse: {
        title: 'Email Already in Use',
        message:
          'The email address is already associated with another account.',
      },
      accountExistsWithDifferentCredential: {
        title: 'Account Already Exists',
        message:
          'An account with this email already exists. Please sign in with the method you originally used to create your account.',
      },
      userNotFound: {
        title: 'User Not Found',
        message:
          'No user account matches the provided details. Please sign up or try again.',
      },
      wrongPassword: {
        title: 'Incorrect Password',
        message: 'The password entered is incorrect. Please try again.',
      },
      networkRequestFailed: {
        title: 'Offline',
        message:
          'You appear to be offline. Please check your internet connection and try again.',
      },
      requiresRecentLogin: {
        title: 'Session Expired',
        message: 'For security reasons, please log in again to proceed.',
      },
      apiKeyNotValid: {
        title: 'Configuration Error',
        message:
          'The app is not configured correctly. Please contact the developer for assistance.',
      },
      tooManyRequests: {
        title: 'Too Many Requests',
        message:
          'You have made too many requests. Please wait a while before trying again.',
      },
      accountCreationLimitExceeded: {
        title: 'Rate Limit Exceeded',
        message:
          'You have exceeded the rate limit for account creation. Please try again later.',
      },
      signOutFailed: {
        title: 'Sign Out Failed',
        message: 'There was an issue signing you out. Please try again.',
      },
      userIsNull: {
        title: 'User Not Found',
        message:
          'Your account could not be identified. Please restart the app.',
      },
      credentialAlreadyInUse: {
        title: 'Already Connected',
        message:
          'This account is already linked to another Kiroku account. Sign out and sign in with that account instead.',
      },
      noSuchProvider: {
        title: 'Not Connected',
        message: "That sign-in method isn't connected to your account.",
      },
      lastProvider: {
        title: "Can't Remove",
        message:
          "You can't disconnect your last sign-in method, or you'd be locked out. Connect another method first.",
      },
    },
    database: {
      dataFetchFailed: {
        title: 'Data Fetch Failed',
        message:
          'An error occurred while fetching data. Please try again later.',
      },
      outdatedAppVersion: {
        title: 'Outdated App Version',
        message:
          'Your app version is outdated. Please update to the latest version.',
      },
      searchFailed: {
        title: 'Search Failed',
        message: 'Failed to search the database. Please try again.',
      },
      userCreationFailed: {
        title: 'User Creation Failed',
        message:
          'There was an issue creating the user account. Please try again.',
      },
    },
    homeScreen: {
      title: {
        title: 'Home Screen Error',
        message: 'Failed to load the home screen.',
      },
      noLiveSession: {
        title: 'No Live Session',
        message: "You're not currently in a session.",
      },
    },
    imageUpload: {
      fetchFailed: {
        title: 'Image Upload Failed',
        message: 'Could not fetch the image. Please try again.',
      },
      uploadFailed: {
        title: 'Image Upload Failed',
        message: 'There was an error uploading your image. Please try again.',
      },
      choiceFailed: {
        title: 'Image Choice Failed',
        message:
          'There was an error when selecting your image. Please try again.',
      },
    },
    onyx: {
      generic: {
        title: 'Database error',
        message: 'Failed to reach the local database',
      },
    },
    session: {
      discardFailed: {
        title: 'Session Discard Error',
        message: 'Failed to discard the session. Please try again.',
      },
      loadFailed: {
        title: 'Session Load Error',
        message: 'Failed to load the session. Please try again.',
      },
      saveFailed: {
        title: 'Session Save Error',
        message: 'Failed to save the session. Please try again.',
      },
      startFailed: {
        title: 'Session Start Error',
        message: 'Failed to start a new session.',
      },
    },
    user: {
      bugSubmissionFailed: {
        title: 'Bug Submission Failed',
        message: 'There was an issue submitting the bug. Please try again.',
      },
      couldNotBlockUser: {
        title: 'Could Not Block User',
        message:
          'There was an issue trying to block this user. Please try again.',
      },
      couldNotReportUser: {
        title: 'Could Not Report User',
        message:
          'There was an issue trying to report this user. Please try again.',
      },
      couldNotUnfriend: {
        title: 'Could Not Unfriend',
        message:
          'There was an issue trying to unfriend this user. Please try again.',
      },
      dataFetchFailed: {
        title: 'Data Fetch Failed',
        message: 'Failed to fetch user data. Please try reloading the page.',
      },
      feedbackRemovalFailed: {
        title: 'Feedback Removal Failed',
        message: 'There was an issue removing the feedback. Please try again.',
      },
      feedbackSubmissionFailed: {
        title: 'Feedback Submission Failed',
        message:
          'There was an issue submitting the feedback. Please try again.',
      },
      friendRequestSendFailed: {
        title: 'Friend Request Failed',
        message:
          'There was an issue sending the friend request. Please try again.',
      },
      friendRequestAcceptFailed: {
        title: 'Friend Request Failed',
        message:
          'There was an issue accepting the friend request. Please try again.',
      },
      friendRequestRejectFailed: {
        title: 'Friend Request Failed',
        message:
          'There was an issue removing the friend request. Please try again.',
      },
      nicknameUpdateFailed: {
        title: 'Nickname Update Failed',
        message: 'There was an error updating your nickname. Please try again.',
      },
      statusUpdateFailed: {
        title: 'Status Update Failed',
        message: 'There was an error updating your status. Please try again.',
      },
      themeUpdateFailed: {
        title: 'Theme Update Failed',
        message: 'There was an error updating your theme. Please try again.',
      },
      timezoneUpdateFailed: {
        title: 'Timezone Update Failed',
        message: 'There was an error updating your timezone. Please try again.',
      },
      usernameUpdateFailed: {
        title: 'Username Update Failed',
        message: 'There was an error updating your username. Please try again.',
      },
    },
    generic: {
      title: 'Error',
      message: 'An error occurred.',
    },
    permissionDenied: {
      title: 'Permission Denied',
      message:
        'You do not have the necessary permissions. Contact the administrator for help.',
    },
    unknown: {
      title: 'Unknown Error',
      message: 'An unknown error occurred.',
    },
  },
} satisfies TranslationBase;
